import React, { useContext } from 'react';
import {
  Button,
  Image,
  Link,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

// "Signing in with a work account" help article. The locale segment is replaced at runtime with the
// current UI locale from server context (e.g. en-US -> "en").
const WORK_ACCOUNT_HELP_ARTICLE = '60043/-1';

// Derives the help-center locale segment from the server-provided CurrentLocale (e.g. "en-US" ->
// "en"), defaulting to English. Help center deep-links are locale-scoped (…/apex/ads/{locale}/…).
const getHelpLocale = (): string => {
  const locale = (window as any)?.serverContext?.CurrentLocale;
  if (typeof locale === 'string' && locale) {
    return locale.toLowerCase().split('-')[0];
  }
  return 'en';
};

export type ActivationAadOnlyRequiredProps = {
  // Inviting customer's name (Validate `customerName`). Shown inline when available.
  customerName?: string | null;
  // Inviting customer's number. Not yet returned by Validate — shown inline in parentheses only
  // when available; the copy omits it otherwise.
  customerNumber?: string | null;
  // "Continue with Microsoft" — re-authenticate with a Microsoft work/school (AAD) account.
  onContinueWithMicrosoft: () => void;
  // "Go back" — sign the current session out and return to the invitation choice view.
  onGoBack: () => void;
};

// Shown on the post-auth page when the invitation is AAD-only (the inviting customer disallows
// personal Microsoft accounts — Validate `invitationFlags.msaSupported === false`) but the user
// signed in with a non-AAD identity. To accept the invitation they must re-authenticate with a
// Microsoft work or school (AAD) account. The button behavior mirrors the pre-auth AAD-only view.
export const ActivationAadOnlyRequired: React.FC<ActivationAadOnlyRequiredProps> = ({
  customerName,
  customerNumber,
  onContinueWithMicrosoft,
  onGoBack,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();
  const workAccountHelpUrl = `https://help.ads.microsoft.com/#apex/ads/${getHelpLocale()}/${WORK_ACCOUNT_HELP_ARTICLE}`;

  // The customer name (and number, when available) prefixes the requirement sentence. Each case is a
  // complete localizable sentence so translators can reorder freely; the trailing "Learn More" link
  // is appended as a separate element (it must be a React node, not a plain string).
  let requirementLine: string;
  if (customerName && customerNumber) {
    requirementLine = i18n.getString(
      _TL_('{{customerName}} ({{customerNumber}}) now requires you to sign in with a work or school account. If you don\'t have one, reach out to your organization\'s IT admin.'),
      { customerName, customerNumber }
    );
  } else if (customerName) {
    requirementLine = i18n.getString(
      _TL_('{{customerName}} now requires you to sign in with a work or school account. If you don\'t have one, reach out to your organization\'s IT admin.'),
      { customerName }
    );
  } else {
    requirementLine = i18n.getString(_TL_('This account now requires you to sign in with a work or school account. If you don\'t have one, reach out to your organization\'s IT admin.'));
  }

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-aad-only-title"
        aria-describedby="activation-aad-only-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-aad-only-title" className={classes.errorTitle}>
          {i18n.getString(_TL_('Use your Microsoft work or school account now'))}
        </Title3>

        <div id="activation-aad-only-description" className={classes.errorBody}>
          <Text block>
            {requirementLine}
            {' '}
            <Link
              className="aad-only-learn-more"
              href={workAccountHelpUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.getString(_TL_('Learn More'))}
            </Link>
          </Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="aad-only-continue"
            appearance="primary"
            onClick={onContinueWithMicrosoft}
          >
            {i18n.getString(_TL_('Continue with Microsoft'))}
          </Button>
          <Button
            className="aad-only-go-back"
            appearance="secondary"
            onClick={onGoBack}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
